import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Grow,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import elkLayouts from "@mermaid-js/layout-elk";
import mermaid from "mermaid";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { CollapsibleListItem } from "src/components/ui/CollapsibleListItem";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { getTraceabilityMatrixQueryKey } from "src/config";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  useGetDataKeyValue,
  useGetDevice,
  useGetDevices,
  useGetDocuments,
  useGetDocumentWithAllVersions,
  useGetFile,
  useGetTraceabilityMatrix,
  useGlobalDeviceContext,
} from "src/hooks";
import { getRelevantDocVersion } from "src/utils";
import RawEditor from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { FilePreview } from "src/components/DocViewer/components/fileDocViewer/FilePreview";
import { useCrisp } from "src/hooks/Crisp";
import { fillRouteWithSlugs } from "src/utils/navigation";
import {
  deleteRiskControlSsrLink,
  deleteSsrDesignFileLink,
  deleteTestValidationFileLink,
  postRiskControlSsrLink,
  postSsrDesignFileLink,
  postTestValidationFileLink,
} from "src/services";
import type {
  TraceabilityChain,
  TraceabilityRelation,
} from "src/stores/models";

type SelectOption = {
  value: string;
  label: string;
};

type LinkRelation = "risk" | "design" | "validation";

type LinkManagerRow = {
  id: string;
  relation: LinkRelation;
  sourceId: string;
  sourceLabel: string;
  targetId: string;
  targetLabel: string;
};

type PreviewTarget =
  | { type: "dataKey"; dataKey: string; displayId: string }
  | { type: "document"; documentId: string };

const FILE_DOC_PREFIX = "FILE_DOC:";
const RC_ID_REGEX = /\bRC\d+\b/gi;

const LINK_TYPE_OPTIONS: Array<{
  value: LinkRelation;
  label: string;
  shortLabel: string;
  sourceLabel: string;
  targetLabel: string;
  chipBg: string;
  chipFg: string;
  sourceDataKey: string | null;
  targetDataKey: string | null;
}> = [
  {
    value: "risk",
    label: "RC → SSR (Risk Control → Subsystem Requirement)",
    shortLabel: "RC → SSR",
    sourceLabel: "Risk control",
    targetLabel: "Subsystem requirement",
    chipBg: "#FFF7ED",
    chipFg: "#9A3412",
    sourceDataKey: "risk-controls",
    targetDataKey: "subsystem-requirements",
  },
  {
    value: "design",
    label: "SSR → DSF (Subsystem Requirement → Design Spec)",
    shortLabel: "SSR → DSF",
    sourceLabel: "Subsystem requirement",
    targetLabel: "Design spec",
    chipBg: "#EEF2FF",
    chipFg: "#3730A3",
    sourceDataKey: "subsystem-requirements",
    targetDataKey: null,
  },
  {
    value: "validation",
    label: "ST → VF (System Test → Validation File)",
    shortLabel: "ST → VF",
    sourceLabel: "System test",
    targetLabel: "Validation file",
    chipBg: "#F0FDF4",
    chipFg: "#166534",
    sourceDataKey: "system-test-plan",
    targetDataKey: null,
  },
];

const CHAIN_PREFIX_DATA_KEY: Record<string, string> = {
  UN: "user-needs",
  RK: "risk-analysis",
  RC: "risk-controls",
  SSR: "subsystem-requirements",
  ST: "system-test-plan",
};

const dataKeyForChainId = (id: string): string | null => {
  const match = id.match(/^([A-Za-z]+)/);
  if (!match) return null;
  return CHAIN_PREFIX_DATA_KEY[match[1].toUpperCase()] || null;
};

const parseTraceabilityIds = (value: string, regex: RegExp): string[] =>
  Array.from(new Set(value.match(regex) || [])).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );

const parseDocumentId = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  if (value.startsWith(FILE_DOC_PREFIX)) {
    return value.slice(FILE_DOC_PREFIX.length);
  }

  return value;
};

const getChainDisplayId = (chain: TraceabilityChain): string | null => {
  const segments = chain.id.split(":");
  const candidate = segments[segments.length - 1] || "";
  return /^[A-Z]+\d+$/i.test(candidate) ? candidate : null;
};

const NODE_LEGEND: Array<{ kind: string; label: string; color: string; border: string }> = [
  { kind: "UN", label: "User Need", color: "#dbeafe", border: "#1d4ed8" },
  { kind: "SR", label: "System Requirement", color: "#cffafe", border: "#0891b2" },
  { kind: "SSR", label: "Subsystem Requirement", color: "#dcfce7", border: "#16a34a" },
  { kind: "DSF", label: "Design Spec File", color: "#fef3c7", border: "#d97706" },
  { kind: "ST", label: "System Test", color: "#fde68a", border: "#b45309" },
  { kind: "VF", label: "Validation File", color: "#ede9fe", border: "#7c3aed" },
  { kind: "RK", label: "Risk", color: "#ffedd5", border: "#ea580c" },
  { kind: "RC", label: "Risk Control", color: "#ffe4e6", border: "#e11d48" },
  { kind: "—", label: "Missing / Placeholder", color: "#fff1f2", border: "#e11d48" },
];

const ChainLegend = () => (
  <Stack
    direction="row"
    spacing={1.25}
    flexWrap="wrap"
    useFlexGap
    aria-label="Chain node legend"
  >
    {NODE_LEGEND.map((item) => (
      <Tooltip key={item.kind} title={item.label} arrow>
        <Box
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "help" }}
        >
          <Box
            sx={{
              width: 12,
              height: 10,
              borderRadius: 0.25,
              backgroundColor: item.color,
              border: `1px solid ${item.border}`,
              borderStyle: item.kind === "—" ? "dashed" : "solid",
            }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 11 }}>
            {item.kind === "—" ? "Placeholder" : item.kind}
          </Typography>
        </Box>
      </Tooltip>
    ))}
  </Stack>
);

let mermaidInitialized = false;
let mermaidLayoutsRegistered = false;

const NODE_LABEL_COLOR_BY_CLASS: Record<string, string> = {
  typeUN: "#1d4ed8",
  typeSR: "#0891b2",
  typeSSR: "#16a34a",
  typeDESIGN_SPEC: "#d97706",
  typeST: "#b45309",
  typeVT: "#ca8a04",
  typeVALIDATION_FILE: "#7c3aed",
  typeRK: "#ea580c",
  typeRC: "#e11d48",
};

const TRACEABILITY_NODE_COLOR_CSS = Object.entries(NODE_LABEL_COLOR_BY_CLASS)
  .map(
    ([className, color]) => `
.node.${className} .traceability-node-type,
.node.${className} .traceability-node-value {
  color: ${color} !important;
}
`
  )
  .join("");

const TRACEABILITY_MERMAID_THEME_CSS = `
.traceability-node-type {
  display: block;
  font-size: 0.75em;
  font-weight: 700;
  line-height: 1.15;
}

.traceability-node-value {
  display: block;
  min-width: 50px;
  margin-top: 3px;
  font-size: 1em;
  font-weight: 400;
  line-height: 1.25;
}

.node .label p {
  margin: 0;
}

.node .label foreignObject div,
.node .label .nodeLabel,
.node .label .nodeLabel p,
.node .label .traceability-node-type,
.node .label .traceability-node-value {
  text-align: left !important;
}

${TRACEABILITY_NODE_COLOR_CSS}

.node.broken .traceability-node-type,
.node.broken .traceability-node-value,
.node.placeholder .traceability-node-type,
.node.placeholder .traceability-node-value {
  color: #881337 !important;
}
`;

const sanitizeMermaidId = (value: string): string =>
  value.replace(/[^a-zA-Z0-9_]/g, "_");

const getCoveragePercent = (covered: number, total: number): number => {
  if (total === 0) {
    return 0;
  }

  return Math.round((covered / total) * 100);
};

const MermaidDiagram = ({
  definition,
  chartId,
}: {
  definition: string;
  chartId: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;

    const renderDiagram = async () => {
      if (!containerRef.current) {
        return;
      }

      if (!mermaidInitialized) {
        if (!mermaidLayoutsRegistered) {
          mermaid.registerLayoutLoaders(elkLayouts);
          mermaidLayoutsRegistered = true;
        }
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "loose",
          theme: "default",
          themeCSS: TRACEABILITY_MERMAID_THEME_CSS,
          elk: {
            considerModelOrder: "NODES_AND_EDGES",
            forceNodeModelOrder: true,
            mergeEdges: true,
          },
          flowchart: {
            curve: "stepBefore",
            defaultRenderer: "elk",
            htmlLabels: false,
            padding: 4,
            nodeSpacing: 10,
            rankSpacing: 20,
            useMaxWidth: false,
          },
        });
        mermaidInitialized = true;
      }

      try {
        const { svg, bindFunctions } = await mermaid.render(
          `traceability_${sanitizeMermaidId(chartId)}_${Date.now()}`,
          definition
        );

        if (isCancelled || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = svg;
        const renderedSvg = containerRef.current.querySelector("svg");
        if (renderedSvg) {
          renderedSvg.style.display = "block";
          renderedSvg.style.width = "max-content";
          renderedSvg.style.maxWidth = "100%";
          renderedSvg.style.height = "auto";
        }
        bindFunctions?.(containerRef.current);
      } catch {
        if (!isCancelled && containerRef.current) {
          containerRef.current.innerHTML =
            '<div class="text-xs text-rose-700">Unable to render diagram.</div>';
        }
      }
    };

    renderDiagram();

    return () => {
      isCancelled = true;
    };
  }, [chartId, definition]);

  return <div data-testid="mermaid-diagram" ref={containerRef} />;
};

const LinkAutocomplete = ({
  label,
  options,
  value,
  onChange,
  disabled,
  previewTargetForOption,
  onPreview,
}: {
  label: string;
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
  previewTargetForOption?: (optionValue: string) => PreviewTarget | null;
  onPreview?: (target: PreviewTarget) => void;
}) => (
  <Autocomplete
    options={options}
    getOptionLabel={(option) => option.label}
    isOptionEqualToValue={(option, nextValue) => option.value === nextValue.value}
    value={options.find((option) => option.value === value) || null}
    onChange={(_, nextValue) => onChange(nextValue?.value || null)}
    renderOption={(props, option) => {
      const { key: _omitKey, ...rest } = props as typeof props & { key?: React.Key };
      return (
        <li
          {...rest}
          key={option.value}
          style={{ ...rest.style, display: "flex", alignItems: "center", gap: 4 }}
        >
          <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
            {option.label}
          </Box>
          {onPreview && previewTargetForOption && (() => {
            const target = previewTargetForOption(option.value);
            if (!target) return null;
            return (
              <IconButton
                size="small"
                aria-label={`Preview ${option.label}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onPreview(target);
                }}
                sx={{ width: 24, height: 24, flexShrink: 0 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            );
          })()}
        </li>
      );
    }}
    renderInput={(params) => (
      <TextField {...params} size="small" label={label} placeholder={`Select ${label}`} />
    )}
    disabled={disabled}
  />
);

const PreviewableId = ({
  id,
  label,
  target,
  onPreview,
  monospace,
  muted,
}: {
  id: string;
  label?: string;
  target: PreviewTarget | null;
  onPreview: (target: PreviewTarget) => void;
  monospace?: boolean;
  muted?: boolean;
}) => (
  <Box
    component="button"
    type="button"
    onClick={() => target && onPreview(target)}
    disabled={!target}
    sx={{
      background: "none",
      border: "none",
      p: 0,
      m: 0,
      textAlign: "left",
      cursor: "pointer",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontFamily: monospace ? "ui-monospace, monospace" : "inherit",
      fontSize: monospace ? 12 : 12.5,
      fontWeight: monospace ? 500 : 400,
      color: muted ? "text.secondary" : "#334155",
      "&:hover": { textDecoration: "underline", color: "primary.main" },
    }}
  >
    {label ?? id}
  </Box>
);

const ChainLinkManager = memo(
  ({
    chain,
    relations,
    riskControlOptions,
    designDocumentOptions,
    validationDocumentOptions,
    onSaveRiskLink,
    onDeleteRiskLink,
    onSaveDesignLink,
    onDeleteDesignLink,
    onSaveValidationLink,
    onDeleteValidationLink,
    onPreview,
    isSaving,
  }: {
    chain: TraceabilityChain;
    relations: TraceabilityRelation[];
    riskControlOptions: SelectOption[];
    designDocumentOptions: SelectOption[];
    validationDocumentOptions: SelectOption[];
    onSaveRiskLink: (params: {
      mode: "create";
      rcId: string;
      ssrId: string;
    }) => Promise<unknown>;
    onDeleteRiskLink: (params: { rcId: string; ssrId: string }) => void;
    onSaveDesignLink: (params: {
      mode: "create";
      ssrId: string;
      documentId: string;
    }) => Promise<unknown>;
    onDeleteDesignLink: (params: { ssrId: string; documentId: string }) => void;
    onSaveValidationLink: (params: {
      mode: "create";
      testId: string;
      documentId: string;
    }) => Promise<unknown>;
    onDeleteValidationLink: (params: {
      testId: string;
      documentId: string;
    }) => void;
    onPreview: (target: PreviewTarget) => void;
    isSaving: boolean;
  }) => {
    const [relation, setRelation] = useState<LinkRelation>("risk");
    const [sourceId, setSourceId] = useState<string | null>(null);
    const [targetId, setTargetId] = useState<string | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const ssrOptions = useMemo(
      () =>
        Array.from(new Set(chain.ssrIds))
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          .map((id) => ({ value: id, label: id })),
      [chain.ssrIds]
    );

    const testOptions = useMemo(
      () =>
        Array.from(new Set(chain.testIds))
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          .map((id) => ({ value: id, label: id })),
      [chain.testIds]
    );

    const { sourceOptions, targetOptions, sourceLabel, targetLabel } = useMemo(() => {
      const typeEntry = LINK_TYPE_OPTIONS.find((option) => option.value === relation)!;
      if (relation === "risk") {
        return {
          sourceOptions: riskControlOptions,
          targetOptions: ssrOptions,
          sourceLabel: typeEntry.sourceLabel,
          targetLabel: typeEntry.targetLabel,
        };
      }
      if (relation === "design") {
        return {
          sourceOptions: ssrOptions,
          targetOptions: designDocumentOptions,
          sourceLabel: typeEntry.sourceLabel,
          targetLabel: typeEntry.targetLabel,
        };
      }
      return {
        sourceOptions: testOptions,
        targetOptions: validationDocumentOptions,
        sourceLabel: typeEntry.sourceLabel,
        targetLabel: typeEntry.targetLabel,
      };
    }, [
      relation,
      riskControlOptions,
      ssrOptions,
      designDocumentOptions,
      testOptions,
      validationDocumentOptions,
    ]);

    const allRows = useMemo<LinkManagerRow[]>(() => {
      const chainSsrIds = new Set(chain.ssrIds);
      const chainTestIds = new Set(chain.testIds);

      return relations
        .flatMap<LinkManagerRow>((item): LinkManagerRow[] => {
          if (
            item.relation === "RC_TO_SSR" &&
            item.source.traceabilityId &&
            item.target.traceabilityId &&
            chainSsrIds.has(item.target.traceabilityId)
          ) {
            return [
              {
                id: item.id,
                relation: "risk",
                sourceId: item.source.traceabilityId,
                sourceLabel: item.source.label,
                targetId: item.target.traceabilityId,
                targetLabel: item.target.label,
              },
            ];
          }

          if (
            item.relation === "SSR_TO_DESIGN_SPEC" &&
            item.source.traceabilityId &&
            chainSsrIds.has(item.source.traceabilityId)
          ) {
            const documentId = parseDocumentId(item.target.traceabilityId);
            if (!documentId) {
              return [];
            }

            return [
              {
                id: item.id,
                relation: "design",
                sourceId: item.source.traceabilityId,
                sourceLabel: item.source.label,
                targetId: documentId,
                targetLabel: item.target.label,
              },
            ];
          }

          if (
            item.relation === "TEST_TO_VALIDATION_FILE" &&
            item.source.traceabilityId &&
            chainTestIds.has(item.source.traceabilityId)
          ) {
            const documentId = parseDocumentId(item.target.traceabilityId);
            if (!documentId) {
              return [];
            }

            return [
              {
                id: item.id,
                relation: "validation",
                sourceId: item.source.traceabilityId,
                sourceLabel: item.source.label,
                targetId: documentId,
                targetLabel: item.target.label,
              },
            ];
          }

          return [];
        })
        .sort(
          (a, b) =>
            a.relation.localeCompare(b.relation) ||
            a.sourceLabel.localeCompare(b.sourceLabel, undefined, { numeric: true }) ||
            a.targetLabel.localeCompare(b.targetLabel, undefined, { numeric: true })
        );
    }, [
      chain.ssrIds,
      chain.testIds,
      relations,
    ]);

    const resetForm = () => {
      setSourceId(null);
      setTargetId(null);
    };

    const onRelationChange = (value: LinkRelation) => {
      setRelation(value);
      resetForm();
    };

    const handleAdd = async () => {
      if (!sourceId || !targetId) {
        return;
      }
      if (relation === "risk") {
        await onSaveRiskLink({ mode: "create", rcId: sourceId, ssrId: targetId });
      } else if (relation === "design") {
        await onSaveDesignLink({
          mode: "create",
          ssrId: sourceId,
          documentId: targetId,
        });
      } else {
        await onSaveValidationLink({
          mode: "create",
          testId: sourceId,
          documentId: targetId,
        });
      }
      resetForm();
    };

    const handleDelete = (row: LinkManagerRow) => {
      if (row.relation === "risk") {
        onDeleteRiskLink({ rcId: row.sourceId, ssrId: row.targetId });
        return;
      }
      if (row.relation === "design") {
        onDeleteDesignLink({ ssrId: row.sourceId, documentId: row.targetId });
        return;
      }
      onDeleteValidationLink({ testId: row.sourceId, documentId: row.targetId });
    };

    const gridTemplate = "170px 150px 22px 1fr 30px";

    const buildTargetForSide = (
      rowRelation: LinkRelation,
      side: "source" | "target",
      id: string
    ): PreviewTarget | null => {
      const meta = LINK_TYPE_OPTIONS.find((o) => o.value === rowRelation)!;
      const dataKey = side === "source" ? meta.sourceDataKey : meta.targetDataKey;
      if (dataKey) return { type: "dataKey", dataKey, displayId: id };
      return { type: "document", documentId: id };
    };

    const sourcePreviewTargetForOption = (optionValue: string) =>
      buildTargetForSide(relation, "source", optionValue);
    const targetPreviewTargetForOption = (optionValue: string) =>
      buildTargetForSide(relation, "target", optionValue);

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setIsExpanded((current) => !current)}
          sx={{
            px: 1.75,
            py: 1.25,
            borderBottom: isExpanded ? "1px solid" : "none",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 500 }}>
              Links{allRows.length > 0 ? ` (${allRows.length})` : ""}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 0.25, display: "block" }}
            >
              Connect risk controls, design specs, and validation files to this chain.
            </Typography>
          </Box>
          <ExpandMoreIcon
            sx={{
              color: "text.secondary",
              transition: "transform 0.2s",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            gap: 1,
            px: 1.75,
            py: 1,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "text.secondary",
            fontWeight: 500,
            backgroundColor: "#FAFBFC",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>Link type</Box>
          <Box>Source</Box>
          <Box />
          <Box>Target</Box>
          <Box />
        </Box>

        {allRows.length === 0 && (
          <Box
            sx={{
              px: 1.75,
              py: 2.75,
              textAlign: "center",
              color: "text.secondary",
              fontSize: 12.5,
              fontStyle: "italic",
            }}
          >
            No links yet. Pick a link type below and add one.
          </Box>
        )}

        {allRows.map((row) => {
          const meta = LINK_TYPE_OPTIONS.find((option) => option.value === row.relation)!;
          return (
            <Box
              key={row.id}
              sx={{
                display: "grid",
                gridTemplateColumns: gridTemplate,
                gap: 1,
                alignItems: "center",
                px: 1.75,
                py: 1,
                fontSize: 13,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                component="span"
                sx={{
                  justifySelf: "start",
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1,
                  py: 0.25,
                  borderRadius: 0.75,
                  fontSize: 11,
                  fontWeight: 500,
                  lineHeight: 1.7,
                  backgroundColor: meta.chipBg,
                  color: meta.chipFg,
                  whiteSpace: "nowrap",
                }}
              >
                {meta.shortLabel}
              </Box>
              <PreviewableId
                id={row.sourceId}
                label={row.sourceLabel}
                target={buildTargetForSide(row.relation, "source", row.sourceId)}
                onPreview={onPreview}
                monospace
              />
              <Box sx={{ display: "inline-flex", justifyContent: "center", color: "text.disabled" }}>
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Box>
              <PreviewableId
                id={row.targetId}
                label={row.targetLabel}
                target={buildTargetForSide(row.relation, "target", row.targetId)}
                onPreview={onPreview}
                muted
              />
              <Box sx={{ display: "inline-flex", justifyContent: "flex-end" }}>
                <IconButton
                  aria-label={`remove link ${row.sourceLabel} to ${row.targetLabel}`}
                  onClick={() => handleDelete(row)}
                  disabled={isSaving}
                  size="small"
                  sx={{ width: 26, height: 26 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "170px 1fr 22px 1fr auto",
            gap: 1,
            alignItems: "center",
            px: 1.75,
            py: 1.25,
            backgroundColor: "#FAFBFC",
          }}
        >
          <Autocomplete
            size="small"
            options={LINK_TYPE_OPTIONS}
            getOptionLabel={(option) => option.shortLabel}
            isOptionEqualToValue={(option, next) => option.value === next.value}
            value={LINK_TYPE_OPTIONS.find((option) => option.value === relation) || undefined}
            onChange={(_, next) => next && onRelationChange(next.value)}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} size="small" label="Link type" />
            )}
            disabled={isSaving}
          />
          <LinkAutocomplete
            label={sourceLabel}
            options={sourceOptions}
            value={sourceId}
            onChange={setSourceId}
            disabled={isSaving}
            previewTargetForOption={sourcePreviewTargetForOption}
            onPreview={onPreview}
          />
          <Box sx={{ display: "inline-flex", justifyContent: "center", color: "text.disabled" }}>
            <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </Box>
          <LinkAutocomplete
            label={targetLabel}
            options={targetOptions}
            value={targetId}
            onChange={setTargetId}
            disabled={isSaving}
            previewTargetForOption={targetPreviewTargetForOption}
            onPreview={onPreview}
          />
          <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleAdd}
              disabled={isSaving || !sourceId || !targetId}
            >
              Add link
            </Button>
        </Box>
        </Collapse>
      </Box>
    );
  }
);

const SummaryStat = ({
  label,
  value,
  meta,
  metaTooltip,
  progress,
  emphasize,
  first,
}: {
  label: string;
  value: string;
  meta?: string;
  metaTooltip?: string;
  progress?: number;
  emphasize?: "alert";
  first?: boolean;
}) => {
  const barColor =
    progress === undefined
      ? undefined
      : progress >= 80
      ? "#16A34A"
      : progress >= 50
      ? "#F59E0B"
      : "#DC2626";

  return (
    <Box
      sx={{
        pl: first ? 0 : 2.5,
        pr: 2.5,
        py: 0.5,
        borderLeft: first ? "none" : "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 600,
          lineHeight: 1.1,
          letterSpacing: "-0.01em",
          color: emphasize === "alert" ? "#B91C1C" : "text.primary",
        }}
      >
        {value}
      </Typography>
      {progress !== undefined && (
        <Box
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 1,
            backgroundColor: "#FEE2E2",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progress}%`,
              backgroundColor: barColor,
              borderRadius: 1,
              transition: "width .4s",
            }}
          />
        </Box>
      )}
      {meta && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: progress !== undefined ? 0.75 : 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {meta}
          </Typography>
          {metaTooltip && (
            <Tooltip title={metaTooltip} arrow>
              <HelpOutlineIcon
                sx={{
                  fontSize: 14,
                  color: "text.secondary",
                  cursor: "help",
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
};

const PanelShell = ({
  title,
  kindBadge,
  onClose,
  children,
  monospaceTitle,
}: {
  title: string;
  kindBadge: { shortKind: string; bg: string; fg: string } | null;
  onClose: () => void;
  children: React.ReactNode;
  monospaceTitle?: boolean;
}) => (
  <Box
    sx={{
      width: 520,
      flex: "0 0 520px",
      borderLeft: "1px solid",
      borderColor: "divider",
      backgroundColor: "#FCFCFC",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {kindBadge && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 0.75,
              py: 0.25,
              borderRadius: 0.75,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: kindBadge.bg,
              color: kindBadge.fg,
              flexShrink: 0,
            }}
          >
            {kindBadge.shortKind}
          </Box>
        )}
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: monospaceTitle ? "ui-monospace, monospace" : "inherit",
            fontWeight: 600,
            fontSize: 14,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      </Stack>
      <IconButton size="small" aria-label="Close details" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {children}
    </Box>
  </Box>
);

const DataKeyPanel = ({
  target,
  orgId,
  deviceId,
  onClose,
}: {
  target: Extract<PreviewTarget, { type: "dataKey" }>;
  orgId: string;
  deviceId: string;
  onClose: () => void;
}) => {
  const { data, isLoading, isError, error } = useGetDataKeyValue({
    orgId,
    deviceId,
    dataKey: target.dataKey,
  });

  return (
    <PanelShell
      title={target.displayId}
      monospaceTitle
      kindBadge={{ shortKind: target.dataKey, bg: "#EEF2FF", fg: "#3730A3" }}
      onClose={onClose}
    >
      {isLoading && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          Loading…
        </Typography>
      )}
      {isError && (
        <Typography variant="body2" sx={{ color: "error.main", px: 2, py: 1.75 }}>
          {error instanceof Error ? error.message : "Failed to load data key."}
        </Typography>
      )}
      {!isLoading && !isError && data !== undefined && !data && (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          No value available for {target.dataKey}.
        </Typography>
      )}
      {!isLoading && !isError && !!data && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <RawEditor
            key={`${target.dataKey}:${target.displayId}`}
            markdown={data}
            isReadOnly
            hideToolbar
            removeBorder
            onChange={() => {}}
          />
        </Box>
      )}
    </PanelShell>
  );
};

const DocumentFilePreview = ({
  orgId,
  fileId,
  file,
}: {
  orgId: string;
  fileId: string;
  file: NonNullable<import("src/stores/models").DocumentVersion["file"]>;
}) => {
  const { data: fileBlob } = useGetFile({ orgId, fileId });
  if (!fileBlob?.data) {
    return (
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", px: 2, py: 1.75 }}
      >
        Loading file…
      </Typography>
    );
  }
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1.75 }}>
      <FilePreview file={file} fileBlob={fileBlob.data} />
    </Box>
  );
};

const DocumentPanel = ({
  target,
  orgId,
  onClose,
}: {
  target: Extract<PreviewTarget, { type: "document" }>;
  orgId: string;
  onClose: () => void;
}) => {
  const {
    data: docWithAllVersions,
    isLoading,
    isFetched,
  } = useGetDocumentWithAllVersions(orgId, target.documentId);
  const version = docWithAllVersions
    ? getRelevantDocVersion(docWithAllVersions)
    : undefined;
  const title = docWithAllVersions?.title || target.documentId;
  const isReadyForApproval =
    !!version &&
    (version.readyForApproval === true || typeof version.revision === "number");

  const body = (() => {
    if (isLoading) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          Loading document…
        </Typography>
      );
    }
    if (isFetched && !docWithAllVersions) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          Document not found.
        </Typography>
      );
    }
    if (!docWithAllVersions || !version) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          This document has no content yet.
        </Typography>
      );
    }
    if (docWithAllVersions.userUpload) {
      if (!version.fileId || !version.file) {
        return (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", px: 2, py: 1.75 }}
          >
            No file uploaded yet.
          </Typography>
        );
      }
      return (
        <DocumentFilePreview
          orgId={orgId}
          fileId={version.fileId}
          file={version.file}
        />
      );
    }
    if (!isReadyForApproval) {
      return (
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", px: 2, py: 1.75 }}
        >
          Document is not ready for approval — no preview available.
        </Typography>
      );
    }
    const markdown = version.snapshot ?? version.contentTemplate ?? "";
    return (
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 1.75 }}>
        <RawEditor
          key={`${docWithAllVersions.id}:${version.id}`}
          markdown={markdown}
          isReadOnly
          hideToolbar
          onChange={() => {}}
        />
      </Box>
    );
  })();

  return (
    <PanelShell
      title={title}
      kindBadge={{ shortKind: "DOC", bg: "#fef3c7", fg: "#92400e" }}
      onClose={onClose}
    >
      {body}
    </PanelShell>
  );
};

const ItemDetailPanel = ({
  target,
  orgId,
  deviceId,
  onClose,
}: {
  target: PreviewTarget;
  orgId: string;
  deviceId: string;
  onClose: () => void;
}) => {
  if (target.type === "dataKey") {
    return (
      <DataKeyPanel
        target={target}
        orgId={orgId}
        deviceId={deviceId}
        onClose={onClose}
      />
    );
  }
  return (
    <DocumentPanel
      target={target}
      orgId={orgId}
      onClose={onClose}
    />
  );
};

const TraceabilityMatrixPage = () => {
  const { orgId = "", deviceId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSupportOpen } = useCrisp();
  const { setActiveGlobalDeviceId } = useGlobalDeviceContext();

  const { data: device, isLoading: isLoadingDevice } = useGetDevice({
    orgId,
    deviceId,
  });
  const { data: devices = [] } = useGetDevices({ orgId });
  const { data: matrix, isLoading, isError } = useGetTraceabilityMatrix({
    orgId,
    deviceId,
  });
  const { data: riskControls } = useGetDataKeyValue({
    orgId,
    deviceId,
    dataKey: "risk-controls",
  });
  const { data: documents = [] } = useGetDocuments(orgId, deviceId, {
    enabled: !!orgId && !!deviceId,
  });

  const [showBrokenOnly, setShowBrokenOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isFilterActive = showBrokenOnly;
  const openSearch = () => {
    setSearchOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setSearch("");
  };

  const chains = useMemo(() => matrix?.chains || [], [matrix?.chains]);

  const filteredChains = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return chains.filter((chain) => {
      if (showBrokenOnly && !chain.isBroken) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }

      return [
        chain.title,
        chain.rootLabel,
        getChainDisplayId(chain),
      ]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [chains, search, showBrokenOnly]);

  const documentOptions = useMemo<SelectOption[]>(() => {
    const byId = new Map<string, SelectOption>();
    documents.forEach((document) => {
      if (byId.has(document.id)) {
        return;
      }
      byId.set(document.id, {
        value: document.id,
        label: document.title || document.id,
      });
    });
    return Array.from(byId.values());
  }, [documents]);

  const designDocumentOptions = documentOptions;
  const validationDocumentOptions = documentOptions;

  const riskControlOptions = useMemo<SelectOption[]>(() => {
    return parseTraceabilityIds(riskControls || "", RC_ID_REGEX).map((id) => ({
      value: id,
      label: id,
    }));
  }, [riskControls]);

  const refreshMatrix = async () => {
    await queryClient.invalidateQueries({
      queryKey: getTraceabilityMatrixQueryKey(orgId, deviceId),
    });
  };

  const saveDesignLinksMutation = useMutation({
    mutationFn: async ({
      mode,
      ssrId,
      documentId,
    }: {
      mode: "create" | "delete";
      ssrId: string;
      documentId: string;
    }) => {
      if (mode === "create") {
        await postSsrDesignFileLink({ orgId, deviceId, ssrId, documentId });
        return;
      }
      await deleteSsrDesignFileLink({ orgId, deviceId, ssrId, documentId });
    },
    onSuccess: async () => {
      await refreshMatrix();
    },
  });

  const saveRiskControlLinksMutation = useMutation({
    mutationFn: async ({
      mode,
      rcId,
      ssrId,
    }: {
      mode: "create" | "delete";
      rcId: string;
      ssrId: string;
    }) => {
      if (mode === "create") {
        await postRiskControlSsrLink({ orgId, deviceId, rcId, ssrId });
        return;
      }
      await deleteRiskControlSsrLink({ orgId, deviceId, rcId, ssrId });
    },
    onSuccess: async () => {
      await refreshMatrix();
    },
  });

  const saveValidationLinksMutation = useMutation({
    mutationFn: async ({
      mode,
      testId,
      documentId,
    }: {
      mode: "create" | "delete";
      testId: string;
      documentId: string;
    }) => {
      if (mode === "create") {
        await postTestValidationFileLink({ orgId, deviceId, testId, documentId });
        return;
      }
      await deleteTestValidationFileLink({ orgId, deviceId, testId, documentId });
    },
    onSuccess: async () => {
      await refreshMatrix();
    },
  });

  const totalUserNeedChains = useMemo(
    () => chains.filter((chain) => chain.id.startsWith("UN:")).length,
    [chains]
  );
  const completedUserNeedChains = useMemo(
    () => chains.filter((chain) => chain.id.startsWith("UN:") && !chain.isBroken).length,
    [chains]
  );
  const totalRiskControls = useMemo(() => {
    if (!matrix) {
      return 0;
    }

    return new Set(
      matrix.relations
        .filter(
          (relation) =>
            relation.relation === "RC_TO_SSR" && relation.source.traceabilityId
        )
        .map((relation) => relation.source.traceabilityId as string)
    ).size;
  }, [matrix]);
  const completedRiskControlChains = useMemo(() => {
    if (!matrix) {
      return 0;
    }

    const completeSsrIds = new Set(
      chains.filter((chain) => !chain.isBroken).flatMap((chain) => chain.ssrIds)
    );

    return new Set(
      matrix.relations
        .filter(
          (relation) =>
            relation.relation === "RC_TO_SSR" &&
            relation.source.traceabilityId &&
            relation.target.traceabilityId &&
            completeSsrIds.has(relation.target.traceabilityId)
        )
        .map((relation) => relation.source.traceabilityId as string)
    ).size;
  }, [chains, matrix]);
  const combinedCompleted = completedUserNeedChains + completedRiskControlChains;
  const combinedTotal = totalUserNeedChains + totalRiskControls;
  const isSavingLink =
    saveDesignLinksMutation.isPending ||
    saveRiskControlLinksMutation.isPending ||
    saveValidationLinksMutation.isPending;

  return (
    <Box className="flex flex-1 h-full flex-col p-0">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              {
                label:
                  isLoadingDevice || !device ? (
                    <Skeleton width={100} />
                  ) : devices.length > 0 ? (
                    <CompactSelect
                      textSize="medium"
                      value={deviceId}
                      onChange={(nextDeviceId) => {
                        setActiveGlobalDeviceId(nextDeviceId);
                        navigate(
                          fillRouteWithSlugs(ROUTES.TRACEABILITY_MATRIX, {
                            [ROUTE_SLUGS.ORG_ID]: orgId,
                            [ROUTE_SLUGS.DEVICE_ID]: nextDeviceId,
                          })
                        );
                      }}
                      ariaLabel="Traceability device"
                      renderValue={() => device.name}
                    >
                      {devices.map((deviceOption) => (
                        <MenuItem key={deviceOption.id} value={deviceOption.id}>
                          {deviceOption.name}
                        </MenuItem>
                      ))}
                    </CompactSelect>
                  ) : (
                    device.name
                  ),
              },
              { label: "Traceability Matrix" },
            ]}
          />
        }
      />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
      <Box
        className="flex flex-1 flex-col gap-y-2 px-4"
        sx={{ overflowY: "auto", scrollbarGutter: "stable", minWidth: 0, pt: "20px", pb: 2 }}
      >
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => setSupportOpen(true)}>
              Feedback
            </Button>
          }
        >
          This is a beta feature under active development and not yet validated.
        </Alert>

        {isError && (
          <Alert severity="error">
            Unable to load traceability matrix data for this device.
          </Alert>
        )}

        {isLoading && (
          <div className="space-y-3">
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={240} />
            <Skeleton variant="rounded" height={240} />
          </div>
        )}

        {!isLoading && matrix && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1.4fr 1fr 1fr",
                },
                pt: 0.5,
                pb: 1,
              }}
            >
              <SummaryStat
                first
                label="Combined coverage"
                value={`${getCoveragePercent(combinedCompleted, combinedTotal)}%`}
                progress={getCoveragePercent(combinedCompleted, combinedTotal)}
                meta={`${combinedCompleted} of ${combinedTotal} chains complete`}
                metaTooltip="Combined count of user need chains + risk control chains. A risk control chain counts as complete only when the risk control is linked to a user need chain that is itself complete."
              />
              <SummaryStat
                label="User need chains"
                value={`${completedUserNeedChains} / ${totalUserNeedChains}`}
                meta={`${getCoveragePercent(
                  completedUserNeedChains,
                  totalUserNeedChains
                )}% complete`}
              />
              <SummaryStat
                label="Risk control chains"
                value={`${completedRiskControlChains} / ${totalRiskControls}`}
                meta={`${getCoveragePercent(
                  completedRiskControlChains,
                  totalRiskControls
                )}% complete`}
              />
            </Box>

            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              justifyContent="flex-end"
              sx={{
                minHeight: 44,
                height: 44,
                flex: "0 0 44px",
                py: 0,
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: "0 0 auto",
                }}
              >
                <Collapse in={!searchOpen} orientation="horizontal" timeout={220}>
                  <IconButton
                    size="small"
                    aria-label="Search chains"
                    onClick={openSearch}
                    sx={{
                      color: search ? "primary.main" : "text.secondary",
                      backgroundColor: search
                        ? "rgba(7, 78, 232, 0.08)"
                        : "transparent",
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Collapse>
                <Collapse in={searchOpen} orientation="horizontal" timeout={220}>
                  <TextField
                    size="small"
                    hiddenLabel
                    variant="standard"
                    inputRef={searchInputRef}
                    placeholder="Search"
                    sx={{ width: 240 }}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        closeSearch();
                      }
                    }}
                    inputProps={{ "aria-label": "Search chains" }}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        fontSize: 14,
                        "& input": { py: 0, height: 28, lineHeight: "28px" },
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: "rgba(100, 116, 139, 0.9)" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Close search"
                            onClick={closeSearch}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Collapse>
              </Box>
              <IconButton
                size="small"
                aria-label="Filter chains"
                onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                sx={{
                  color: isFilterActive ? "primary.main" : "text.secondary",
                  backgroundColor: isFilterActive
                    ? "rgba(7, 78, 232, 0.08)"
                    : "transparent",
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
              <Popper
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                placement="bottom-start"
                transition
                sx={{ zIndex: (theme) => theme.zIndex.modal }}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
                    <Paper elevation={3} sx={{ mt: 0.5, minWidth: 260 }}>
                      <ClickAwayListener onClickAway={() => setFilterAnchorEl(null)}>
                        <MenuList dense autoFocusItem={false}>
                          <MenuItem
                            onClick={() => {
                              setFilterAnchorEl(null);
                              setShowBrokenOnly((current) => !current);
                            }}
                            selected={showBrokenOnly}
                            sx={{
                              borderRadius: 1,
                              mx: 0.5,
                              my: 0.25,
                              "&.Mui-selected": {
                                backgroundColor: "rgba(7, 78, 232, 0.08)",
                                color: "primary.main",
                                fontWeight: 500,
                              },
                              "&.Mui-selected:hover": {
                                backgroundColor: "rgba(7, 78, 232, 0.14)",
                              },
                            }}
                          >
                            <Typography variant="body2" sx={{ color: "inherit" }}>
                              Show uncompleted chains only
                            </Typography>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Stack>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.5 }}>
              {filteredChains.map((chain) => {
                const mermaidDefinition = chain.mermaidDefinition;
                const chainDisplayId = getChainDisplayId(chain);
                const requirementTitle = chain.rootLabel || chain.title;

                return (
                  <CollapsibleListItem
                    key={chain.id}
                    defaultExpanded={false}
                    dataTestId={`chain-${chain.id}`}
                    header={
                      <>
                        <Box
                          component="button"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            const id = chainDisplayId;
                            if (!id) return;
                            const dataKey = dataKeyForChainId(chain.id);
                            if (!dataKey) return;
                            setPreviewTarget({
                              type: "dataKey",
                              dataKey,
                              displayId: id,
                            });
                          }}
                          disabled={
                            !chainDisplayId || !dataKeyForChainId(chain.id)
                          }
                          sx={{
                            flexShrink: 0,
                            fontFamily: "ui-monospace, monospace",
                            fontSize: 12.5,
                            fontWeight: 500,
                            color: chain.isBroken ? "#B91C1C" : "primary.main",
                            backgroundColor: chain.isBroken ? "#FEE2E2" : "#EBF1FF",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            minWidth: 56,
                            textAlign: "center",
                            border: "none",
                            cursor: chainDisplayId ? "pointer" : "default",
                            "&:hover": chainDisplayId
                              ? { filter: "brightness(0.95)" }
                              : undefined,
                          }}
                        >
                          {chainDisplayId || chain.title}
                        </Box>
                        <Box className="flex-1 min-w-0">
                          <Typography variant="subtitle1" noWrap className="font-medium">
                            {requirementTitle}
                          </Typography>
                        </Box>
                        <Box className="flex items-center gap-2 flex-shrink-0">
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 500,
                              lineHeight: 1.7,
                              whiteSpace: "nowrap",
                              backgroundColor: chain.isBroken ? "#FEE2E2" : "#DCFCE7",
                              color: chain.isBroken ? "#B91C1C" : "#166534",
                            }}
                          >
                            {chain.isBroken ? "Has gaps" : "Complete"}
                          </Box>
                        </Box>
                      </>
                    }
                  >
                      <Stack spacing={1.5}>

                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                            p: 1.25,
                            backgroundColor: "#FBFCFE",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              mb: 1,
                              pb: 1,
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              Traceability chain
                            </Typography>
                            <ChainLegend />
                          </Stack>
                          <Box sx={{ overflowX: "auto" }}>
                            <MermaidDiagram
                              definition={mermaidDefinition}
                              chartId={chain.id}
                            />
                          </Box>
                        </Box>

                        <Box data-testid={`manage-links-${chain.id}`}>
                          <ChainLinkManager
                            chain={chain}
                            relations={matrix.relations}
                            riskControlOptions={riskControlOptions}
                            designDocumentOptions={designDocumentOptions}
                            validationDocumentOptions={validationDocumentOptions}
                            onSaveRiskLink={saveRiskControlLinksMutation.mutateAsync}
                            onDeleteRiskLink={({ rcId, ssrId }) =>
                              saveRiskControlLinksMutation.mutate({
                                mode: "delete",
                                rcId,
                                ssrId,
                              })
                            }
                            onSaveDesignLink={saveDesignLinksMutation.mutateAsync}
                            onDeleteDesignLink={({ ssrId, documentId }) =>
                              saveDesignLinksMutation.mutate({
                                mode: "delete",
                                ssrId,
                                documentId,
                              })
                            }
                            onSaveValidationLink={saveValidationLinksMutation.mutateAsync}
                            onDeleteValidationLink={({ testId, documentId }) =>
                              saveValidationLinksMutation.mutate({
                                mode: "delete",
                                testId,
                                documentId,
                              })
                            }
                            onPreview={setPreviewTarget}
                            isSaving={isSavingLink}
                          />
                        </Box>
                      </Stack>
                  </CollapsibleListItem>
                );
              })}

              {filteredChains.length === 0 && (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    color: "text.secondary",
                    fontSize: 13,
                  }}
                >
                  No chains match your filters.
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
      {previewTarget && (
        <ItemDetailPanel
          target={previewTarget}
          orgId={orgId}
          deviceId={deviceId}
          onClose={() => setPreviewTarget(null)}
        />
      )}
      </Box>
    </Box>
  );
};

export default TraceabilityMatrixPage;
